import type { FieldPath, FieldPathValue, FieldValues, RegisterOptions } from "react-hook-form";
import { useController } from "react-hook-form";

import { FormControlGroup } from "@/design-system/Form";
import type { RadioProps } from "@/design-system/Radio";
import { Radio } from "@/design-system/Radio";

interface FormRadioProps<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
> extends Omit<RadioProps, "onChange" | "checked" | "defaultChecked"> {
  name: TName;
  value: FieldPathValue<TFormValues, TName>;
  label: string;
  rules?: RegisterOptions<TFormValues, TName>;
}

/**
 * Values for radio buttons can only be strings. If you need to use a different type, you have to convert it to a string yourself.
 */
export function FormRadio<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
>({ rules, name, ...props }: FormRadioProps<TFormValues, TName>): React.ReactNode {
  const { field } = useController<TFormValues, TName>({ name, rules });

  return <Radio {...props} {...field} value={props.value} defaultChecked={field.value === props.value} />;
}

type FormRadioGroupProps = {
  children: React.ReactNode;
  layout?: "horizontal" | "vertical";
};

export function FormRadioGroup({ children, layout = "horizontal" }: FormRadioGroupProps): React.ReactNode {
  return <FormControlGroup layout={layout}>{children}</FormControlGroup>;
}
